<template>
  <div class="page" :class="{'is-loading': loading}">
    <ff-skeleton class="page-loading" v-if="loading" :loading="loading" :text="'Loading Your Page'"/>
    <div class="error" v-if="!loading && privateOrNull" data-test="empty-page">
      <h1 class="error-title">Hey there, we can't find what you're looking for. This Page does not exist.</h1>
    </div>
    <div class="page-wrapper" :class="{'disable-scroll': showPreview}" v-if="!loading && page">
      <!-- Page Controls -->
      <div class="page-controls">
        <div class="page-control__container" v-if="page.breadcrumb">
          <div class="page-control__container-crumbs" v-for="crumb in page.breadcrumb" :key="crumb.id">
            <a class="page-controls__breadcrumb" @click="navigateTo(crumb.id)">{{crumb.title}}</a>
            <span class="page-controls__spacer">/</span>
          </div>
          <div class="page-control__container-crumbs">
            <span class="page-controls__breadcrumb">{{page.title}}</span>
          </div>
        </div>
        <div v-else class="page-control__container">
          <!-- spacer for pages without breadcrumbs -->
        </div>
        <div class="page-control__container" v-if="isOwner && !preview">
          <!-- <ff-button :text="'Add Template'" :action="addTemplate" v-if="showTemplate"/> -->
          <ff-context-menu v-if="showTemplate" :items="[{title: 'Recipe'}, {title: 'Workout'}]" :text="'Insert Template'" @action="addTemplate"/>
          <ff-inline-loading v-if="savingTier" :saving="savingTier"/>
          <ff-context-menu :items="tiers" :text="'Publish'" @action="selectTier" v-if="!tier"/>
          <div class="tier-select" v-if="tier">
            <ff-dropdown v-model="tier" :items="tiers" :action="updateTier" ref="tier-selector"/>
            <ff-context-menu
              data-test="page-overflow"
              :items="[{
                title: 'Make Private',
                method: makePrivate,
                icon: 'lock'
              }]"
              :icon="'ellipsis'"
              @action="handleOverflow"/>
          </div>
          <ff-context-menu
            v-if="!tier"
            data-test="page-overflow"
            :items="[{
              title: 'Move to Page',
              method: launchMoveDialog,
              test: 'move-cta',
              icon: 'newspaper'
            }, {
              title: 'Delete Page',
              method: confirmDelete,
              test: 'delete-cta',
              icon: 'trash'
            }]"
            :icon="'ellipsis'"
            @action="handleOverflow"
            @event="setContextEvent"
          />
          <ff-page-search
            v-if="showSearch && contextEvent"
            :e="contextEvent"
            @action="relinkPage"
            @close="closePageSearch"
          />
        </div>
      </div>
      <!-- Page Editor View -->
      <div
        class="page-wrapper__cover"
        v-if="image"
        :style="{'background-image': `url(${image})`}"
        :class="{'is-filled': image, 'is-loading': imageLoading}"
        @mouseenter="cover_hover = true"
        @mouseleave="cover_hover = false">
        <div class="page-wrapper__cover-controls" v-if="isOwner && !preview" :class="{'is-active': cover_hover}">
          <ff-button class="action-remove" :text="'Remove Cover Image'" :action="removeCover"/>
          <image-upload class="page-editor__add-cover" :text="'Replace Cover Image'" :type="2" @upload="handleCoverImage" @started="startedUpload"/>
        </div>
      </div>
      <div v-if="isOwner && !preview" class="page-editor">
        <ff-loading v-if="uploadingImage" :text="'Uploading Image'"/>
        <header class="page-editor__header" @mouseenter="header_active = true" @mouseleave="header_active = false">
          <div class="page-editor__cover" :class="{'is-active' : header_active}" v-if="!image">
            <image-upload class="page-editor__add-cover" :text="'Add Cover Image'" @upload="handleCoverImage" @started="startedUpload"/>
          </div>
          <h1 data-test="page-title" class="page-title" contenteditable @blur="updateTitle($event)" @keypress.enter.prevent="handleTitle">{{formattedTitle}}</h1>
        </header>
        <editor-comp
          v-if="ready"
          ref="editor"
          :data="passData"
          :page_id="page_id"
          @saved="handleSaved"
        />
        <div class="page-control__container">
          <span class="page-controls__saved" v-if="lastSaved">Last Saved <span class="value">{{lastSaved}}</span></span>
          <span v-if="saving"><ff-inline-loading :saving="saving"/>Saving</span>
        </div>
      </div>
      <!-- Page VIEW ONLY View -->
      <div v-else class="page-viewer" :class="{'has-image': image}">
        <h1 data-test="page-title" class="page-title">{{formattedTitle}}</h1>
        <editor-comp v-if="ready" ref="editor" :data="passData" :page_id="page_id" :readonly="true"/>
      </div>
      <section @click="login" data-test="preview-block" class="preview-block" v-if="showPreview">
        <div class="preview-block__container">
          <h1 class="preview-block__title" data-test="page-title--private">
            This Page is for Paid Subscribers.
          </h1>
          <span class="preview-block__cta" :style="{ backgroundColor: uiColor }">{{channel.tiers && channel.tiers['paid'] ? `Subscribe for $${channel.tiers['paid'].price} to Get Unlimited Access` : `Subscribe for Unlimited Access`}}</span>
        </div>
      </section>
    </div>
    <modal-login @callback="handleCallback"/>
    <modal-delete @callback="confirmedDeleted"/>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { deserializeImage, serializeImage } from '../../helpers/storage'
import TEMPLATES from '../../helpers/templates'

import persist from '../../mixins/persist'
import analytics from '../../mixins/analytics'

import firebase from 'firebase/app'
import 'firebase/functions'

import EditorComp from '../_Shared/EditorComp'
import FFLoading from '../DesignSystem/FFLoading'
import FFSkeleton from '../DesignSystem/FFSkeleton'
import FFButton from '../DesignSystem/FFButton'
import FFDropdown from '../DesignSystem/FFDropdown'
import FFContextMenu from '../DesignSystem/FFContextMenu'
import FFPageSearch from '../DesignSystem/FFPageSearch'
import FFInlineLoading from '../DesignSystem/FFInlineLoading'
import ImageUpload from '../_Shared/ImageUpload'
import ModalLogin from '../_Modals/ChannelLogin'
import ModalDelete from '../_Modals/ConfirmDelete'
import { DateTime } from 'luxon'
import _ from 'lodash'

export default {
  name: 'Page',
  mixins: [
    persist,
    analytics
  ],
  props: [
    'preview'
  ],
  components: {
    'editor-comp': EditorComp,
    'ff-loading': FFLoading,
    'ff-skeleton': FFSkeleton,
    'ff-button': FFButton,
    'ff-dropdown': FFDropdown,
    'ff-context-menu': FFContextMenu,
    'ff-page-search': FFPageSearch,
    'ff-inline-loading': FFInlineLoading,
    'image-upload': ImageUpload,
    'modal-login': ModalLogin,
    'modal-delete': ModalDelete,
  },
  data () {
    return {
      loading: true,
      ready: false,
      passData: null,
      isOwner: false,
      tier: '',
      savingTier: false,
      header_active: false,
      image: null,
      cover_hover: false,
      template_added: false,
      imageLoading: true,
      uploadingImage: false,
      lastSaved: null,
      hasContent: null,
      saving: false,
      privateOrNull: false,
      price: 0,
      contextEvent: null,
      showSearch: false
    }
  },
  methods: {
    updatePage () {
      this.ready = false
      this.loading = true
      this.$store.commit('UPDATE_PAGE_ID', this.$route.params.page_id || 'landing')
      this.$store.dispatch('fetchChannel')
      this.$store.dispatch('fetchPage', async (data) => {
        this.loading = false;
        if (data && data.err) {
          data = await this.$store.dispatch('fetchPreview')
          if (!data) return this.nothingHere()
        }

        this.passData = data.content
        this.tier = data.tier
        if (!data.cover_image) {
          this.image = null
          this.imageLoading = false
        }
        if (data.cover_image) {
          await this.updateImage(data.cover_image)
        }

        this.track('[Channel] Page View', {
          page: 'page',
          page_id: this.page_id,
          title: data.title,
          tier: data.tier,
          cover_image: data.cover_image ? true : false
        })

        this.ready = true
      })
    },
    async updateImage (img) {
      this.image = await deserializeImage(img)
      this.imageLoading = false
    },
    nothingHere () {
      this.privateOrNull = true
      this.ready = true
      return
    },
    updateTitle (e) {
      let newTitle = e.target.innerHTML
      this.PagesUpdate(this.channel_id, this.page_id, {
        title: newTitle
      }, () => {
        this.$store.dispatch('fetchPage')
        this.$store.dispatch('fetchChannel')
      })
    },
    selectTier (tier) {
      return this.updateTier(tier.value)
    },
    startedUpload () {
      this.uploadingImage = true
    },
    handleCoverImage (result) {
      if (!result || !result.success) return
      let image = serializeImage(result.file.url)
      this.PagesUpdate(this.channel_id, this.page_id, {
        cover_image: image
      }, () => {
        this.image = result.file.url
        this.uploadingImage = false
      })
    },
    handleTitle (e) {
      e.preventDefault()
      e.stopPropagation()
      return this.$refs.editor.addEnter()
    },
    removeCover () {
      this.PagesUpdate(this.channel_id, this.page_id, {
        cover_image: 0
      }, () => {
        this.image = null
      })
    },
    handleOverflow (item) {
      return item.method()
    },
    confirmDelete (pageID, page, callback) {
      let id = pageID || this.page_id
      let p = page || this.page
      let c = callback || this.confirmedDeleted

      this.$modal.show('page-delete', {
        channel_id: this.channel_id,
        page_id: id,
        page: p,
        callback: c
      })
    },
    confirmedDeleted () {
      this.$router.push('/')
    },
    updateTier (tier) {
      this.savingTier = true
      let length = this.countPublished()
      let updatedTier = tier || this.tier
      this.PagesUpdate(this.channel_id, this.page_id, {
        tier: updatedTier,
        index: length
      }, () => {
        this.track('[Fanfolk] Published Page')
        this.$store.dispatch('fetchChannel')
        this.savingTier = false
        this.tier = updatedTier
      })
    },
    makePrivate () {
      let length = this.countDrafts()
      this.PagesUpdate(this.channel_id, this.page_id, {
        tier: '',
        index: length
      }, () => {
        this.track('[Fanfolk] Made Page Private')
        this.$store.dispatch('fetchChannel')
        this.tier = ''
      })
    },
    countDrafts () {
      if (!this.channel.pages || !this.channel.pages.length) return 0
      let draftCount = 0
      for (let i = 0; i < this.channel.pages.length; i++) {
        if (!this.channel.pages[i].tier || this.channel.pages[i].tier == '') {
          draftCount++
        }
      }
      return draftCount + 1
    },
    countPublished () {
      if (!this.channel.pages || !this.channel.pages.length) return 0
      let publishedCount = 0
      for (let i = 0; i < this.channel.pages.length; i++) {
        if (this.channel.pages[i].tier && this.channel.pages[i].tier != '') {
          publishedCount++
        }
      }
      return publishedCount + 1
    },
    addTemplate (t) {
      if (!t.title) return
      let template = TEMPLATES[t.title.toLowerCase()]
      this.$refs['editor'].addTemplate(template)
      this.template_added = true
    },
    handleSaved () {
      this.lastSaved = DateTime.local().toLocaleString(DateTime.DATETIME_MED)
      this.hasContent = true
    },
    login () {
      this.$modal.show('login')
      this.track('[Channel] Clicked Sign Up', { location: 'page' })
    },
    handleCallback (data) {
      if (!data.exists) {
        return this.$router.push({name: 'channel-sign-up'})
      }
      return this.$router.push({name: 'page', params: { page_id: this.$route.params.page_id }})
    },
    navigateTo (id) {
      return this.$router.push({name: 'page', params: { page_id: id }})
    },
    launchMoveDialog () {
      this.showSearch = true
    },
    setContextEvent(e) {
      this.contextEvent = e
    },
    async relinkPage(page) {
      this.closePageSearch()
      const func = firebase.functions().httpsCallable('movePage')
      await func({channel_id: this.channel_id, page_id: this.page_id, new_parent_id: page.id})
      this.$store.dispatch('fetchPage')
      // TODO - I'm not sure what's happening here, but `movePage` doesn't seem to work as expected
      // this.$store.dispatch('movePage', {page_id: this.page_id, new_parent_id: page.id});
    },
    closePageSearch() {
      this.showSearch = false
      this.contextEvent = null
    }
  },
  created () {
    this.isOwner = this.$store.state.channel.isOwner
  },
  watch: {
    '$route.params.page_id': {
      handler: function () {
        this.image = null
        this.lastSaved = null
        this.privateOrNull = false
        return this.updatePage()
      },
      deep: true,
      immediate: true
    },
    '$store.state.confirm_delete_page': {
      handler: function () {
        let id = this.$store.state.confirm_delete_page
        if (!id) return
        this.confirmDelete(id, { parent: this.page_id }, () => {
          // This just cleans up the states, reloads the page, refetches the content
          // all good things, though we could do these individually as well
          this.$router.go()
        })
      }
    }
  },
  computed: {
    ...mapState(['page', 'page_id', 'channel', 'channel_id', 'tiers', 'user']),
    showTemplate () {
      if (this.hasContent) return false
      if (!this.passData) return true
      if (this.passData !== '<p></p>') return false
      if (this.template_added) return false
      return true
    },
    formattedTitle () {
      return _.unescape(this.page.title)
    },
    uiColor () {
      if (this.channel && this.channel.landing && this.channel.landing.color) {
        return `#${this.channel.landing.color}`
      }
      return `#072FFF`
    },
    showPreview () {
      if (this.isOwner) return false
      if (this.page.tier && this.page.tier === 'public') return false
      if (this.user && this.channel.fans) {
        if (!this.channel.fans[this.user.uid]) return true
        const userTier = this.channel.fans[this.user.uid].tier
        const paidUser = userTier && userTier !== 'free' && userTier !== ''
        if (paidUser) return false
      }
      const readyAndPrivate = this.ready && this.privateOrNull
      const privateTier = this.page.tier && this.page.tier !== 'public' && this.page.tier !== ''
      return readyAndPrivate || privateTier
    }
  }
}
</script>

<style scoped lang="scss">
  @import "../../assets/styles/app.scss";

  h1 {
    font-size: 3rem;
    font-weight: 500;
  }

  .error {
    @include error-title;
  }

  .page {
    text-align: left;
    box-sizing: border-box;
    transition: 1s ease all;
    opacity: 1;

    &.is-loading {
      opacity: 0;
    }
  }

  .page-loading {
    max-width: $maxWidth--wide;
    margin: $unit-3*6 auto 0;
  }

  .page-title {
    font-size: $fontSize-xxl;
    font-weight: 500;
    line-height: .9;
    margin-bottom: $unit-2;
    outline: 0;
  }

  .page-wrapper__cover {
    display: block;
    height: 12rem;
    height: 50vh;
    width: 100%;
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    position: relative;
    opacity: 0;
    transition: .15s ease all;

    &.is-filled {
      opacity: 1;
      transition: .15s ease all;
    }
  }

  .page-wrapper__cover-controls {
    display: flex;
    align-items: flex-end;
    position: absolute;
    bottom: 0;
    right: 0;
    padding: $unit-1;
    transition: .15s ease all;
    opacity: 0;

    &.is-active {
      opacity: 1;
      transition: .3s ease all;
    }

    .action-remove {
      margin: 0 $unit-1 0 0;
    }
  }

  .page-editor,
  .page-viewer {
    padding-top: $unit-3*2;
    max-width: $maxWidth--wide;
    margin: auto;
  }

  .page-editor__header {
    position: relative;
  }

  .page-editor__cover {
    width: 100%;
    opacity: 0;
    transition: .3s ease all;
    margin-bottom: $unit-2;

    &.is-active {
      opacity: 1;
      transition: .3s ease all;
    }
  }

  .page-controls {
    display: flex;
    justify-content: space-between;
    width: 100%;
    padding: $unit-1;
    align-items: middle;

    &__saved {
      @include text();
      font-size: $fontSize-normal;
      margin-bottom: $unit-3;

      .value {
        margin-left: .25em;
        opacity: 0.55;
      }
    }
  }

  .page-control__container {
    display: flex;

    div {
      margin-right: $unit-1;

      &:last-child {
        margin: 0;
      }
    }

    .page-control__container-crumbs {
      margin-right: 0;
    }

    &:first-child {
      margin-left: $unit-2;
    }
  }

  .page-controls__spacer {
    margin-right: .5rem;
    margin-left: .5rem;
    font-size: $fontSize-medium;
    color: $color-darkish;
  }

  .page-control__container-crumbs {
    display: inline-flex;
    align-items:  center;
  }

  .page-controls__breadcrumb {
    @include text();
    color: $color-darkish;
    max-width: 12rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
    line-height: 1;
    border-radius: $borderRadius;
    padding: $unit-1/1.6 $unit-1 $unit-1;

    &:hover {
      color: $color-dark;
      text-decoration: underline;
      cursor: pointer;
      background: $color-almost-white;
    }
  }

  .page-viewer {
    margin-top: 1rem;
  }

  .tier-select {
    display: flex;

    .ff-dropdown,
    .ff-context-menu {
      margin-right: $unit-1;
    }
  }

  .preview-block {
    position: fixed;
    width: 100%;
    bottom: 0;
    text-decoration: none;
    box-sizing: border-box;
    color: $color-dark;
    text-align: center;

    &__container {
      background: $color-almost-white;
      box-sizing: border-box;
      height: 100%;
      width: calc(100% - #{$sidebar-width} - #{$unit-3*2});
      margin: $unit-3;
      border-radius: $borderRadius;
      padding: $unit-2;
    }

    &__title {
      @include text('heading');
      font-size: $fontSize-medium;
      margin-bottom: $unit-1;
    }

    &__cta {
      display: inline-block;
      font-size: $fontSize-normal;
      background: rgba($color-dark, 0.1);
      color: $color-white;
      padding: $unit-1 $unit-2;
      border-radius: $borderRadius;
    }

    &:hover {
      cursor: pointer;
    }

  }

  .disable-scroll {
    height: 100%;
    overflow: hidden;
  }

  @import "../../assets/styles/_mobile.scss";
</style>
